import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
  useNodes,
  useEdges,
} from "@xyflow/react";
import {
  ShieldCheck,
  Trash2,
  Settings,
  X,
  AlertTriangle,
  Loader2,
  CheckCircle,
  PlayCircle,
  FileText,
  Hash,
} from "lucide-react";
import syntaxHighlight from "../../utils/jsonViewer";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";
import { AvailableVariable } from "../../common/ui/types";
import { formatDragVariable } from "../../utils/variableHelper";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";
import NodeDocsButton from "../../docs/NodeDocsButton";
import { PromptEditorModal } from "../../common/ui/PromptEditorModal";
import { Copy, MessageSquare } from "lucide-react";

export const ValidatorNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [inputJson, setInputJson] = useState<string>(data?.input_json || "");
  const [instruction, setInstruction] = useState<string>(data?.instruction || "");
  const [iterationLimit, setIterationLimit] = useState<number>(data?.iteration_limit || 3);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const { setNodes, deleteElements } = useReactFlow();
  const nodes = useNodes();
  const edges = useEdges();

  // Execution Status
  const executionStatus = data?.executionStatus || null;
  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  // Previous nodes for sidebar
  const previousNodes = useMemo(() => {
    const ancestors = getAllAncestorNodes(id, nodes, edges);
    return ancestors.map((n) => ({
      nodeId: n.id,
      nodeName: n.data?.label || n.data?.name || n.id,
      nodeType: n.type || "unknown",
      inputs: n.data?.inputs || {},
      outputs: n.data?.outputs || {},
    })) as AvailableVariable[];
  }, [edges, id, nodes]);

  // Update node data & validate
  useEffect(() => {
    const validationErrors: string[] = [];
    if (!inputJson) validationErrors.push("Input JSON source is required");
    if (!instruction) validationErrors.push("Evaluation instruction is required");
    setErrors(validationErrors);

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== id) return node;
        return {
          ...node,
          data: {
            ...node.data,
            input_json: inputJson,
            instruction,
            iteration_limit: iterationLimit,
          },
        };
      })
    );
  }, [inputJson, instruction, iterationLimit, id, setNodes]);

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent, targetField: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(null);

    try {
      const fullVariable = e.dataTransfer.getData("text/plain");
      if (!fullVariable) return;

      switch (targetField) {
        case "input_json":
          setInputJson(fullVariable);
          break;
        case "instruction":
          setInstruction((prev) => (prev ? `${prev}\n${fullVariable}` : fullVariable));
          break;
      }
    } catch (err) {
      console.error("Failed to parse dropped data", err);
    }
  };

  const handleModalDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output"
  ) => {
    e.stopPropagation();
    const fullVariable = formatDragVariable(nodeName, variable, type);
    e.dataTransfer.setData("text/plain", fullVariable);
    e.dataTransfer.effectAllowed = "copy";
  };

  const hasOutputs = data.outputs && Object.keys(data.outputs).length > 0;

  return (
    <>
      <div
        className={`relative bg-white border-2 rounded-xl min-w-[300px] shadow-sm transition-all duration-200 group
          ${errors.length > 0 && !isExecuting
            ? "border-red-400"
            : isExecuting
              ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
              : executionSuccess
                ? "border-green-500 shadow-md shadow-green-200"
                : executionError
                  ? "border-red-500 shadow-md shadow-red-200"
                  : selected
                    ? "border-blue-500 shadow-lg scale-105"
                    : "border-gray-200 hover:border-violet-300"
          }
        `}
      >
        {/* Execution Spinner */}
        {isExecuting && (
          <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
            <div className="bg-white rounded-full p-3 shadow-lg">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          </div>
        )}

        {/* Success Overlay */}
        {executionSuccess && (
          <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
            <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
          </div>
        )}

        {/* Error Overlay */}
        {executionError && !isExecuting && (
          <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
            <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
              <AlertTriangle className="w-4 h-4 text-white" />
            </div>
          </div>
        )}

        {/* Execute Button */}
        <div className="absolute -top-3 -right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              data.onExecuteNode?.(id);
            }}
            className="p-1.5 bg-blue-500 text-white rounded-full shadow-md hover:bg-blue-600 transition-all"
            title="Execute all nodes up to this node"
          >
            {isExecuting ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <PlayCircle className="w-3 h-3" />
            )}
          </button>
        </div>

        {/* Header */}
        <div
          className={`p-3 rounded-t-xl flex items-center justify-between
            ${executionSuccess
              ? "bg-green-50"
              : executionError
                ? "bg-red-50"
                : "bg-gradient-to-r from-violet-50 to-purple-50"
            }
          `}
        >
          <div className="flex items-center gap-2">
            <div
              className={`p-2 rounded-lg ${executionSuccess
                ? "bg-green-100 text-green-600"
                : executionError
                  ? "bg-red-100 text-red-600"
                  : "bg-white text-violet-600 shadow-sm"
                }`}
            >
              {isExecuting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">
                Validator
              </h3>
              <p className="text-[10px] text-gray-500">
                Data Validation & Verification
              </p>
            </div>
          </div>
          <div className="flex gap-1 items-center">
            <NodeDocsButton nodeType="VALIDATOR" />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteNode();
              }}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-3 space-y-3">
          {/* Configure Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsModalOpen(true);
            }}
            className="w-full py-2.5 rounded-lg bg-violet-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-violet-700 transition-colors"
          >
            <Settings className="w-3.5 h-3.5" /> Configure Node
          </button>

          {/* Inputs Display */}
          <div className="space-y-2">
            <div className="bg-gray-50 rounded-lg p-2 space-y-2 border border-gray-100">
              <div className="flex justify-between items-center mb-1">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                  Inputs
                </p>
                <p className="text-[10px] font-medium text-violet-600">
                  Max {iterationLimit} iterations
                </p>
              </div>

              <div className="grid grid-cols-1 gap-2">
                {inputJson && (
                  <div className="text-[10px] bg-white border px-2 py-1 rounded text-gray-700 truncate flex items-center gap-1">
                    <FileText className="w-3 h-3 text-violet-500" /> {inputJson}
                  </div>
                )}
                {instruction && (
                  <div className="text-[10px] bg-white border px-2 py-1 rounded text-gray-700 truncate flex items-center gap-1">
                    <Hash className="w-3 h-3 text-violet-500" /> {instruction.substring(0, 60)}{instruction.length > 60 ? "..." : ""}
                  </div>
                )}
                {!inputJson && !instruction && (
                  <div className="text-[10px] text-gray-400 italic text-center py-2">
                    Configure inputs in settings
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Validation Errors */}
        {!selected && errors.length > 0 && (
          <div className="absolute -bottom-8 left-0 right-0 flex justify-center">
            <div className="bg-red-100 text-red-600 text-[10px] px-2 py-1 rounded-full shadow-sm flex items-center gap-1 border border-red-200">
              <AlertTriangle className="w-3 h-3" />
              {errors.length} Error{errors.length > 1 ? "s" : ""}
            </div>
          </div>
        )}

        {/* Input Handle */}
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 bg-violet-600 border-2 border-white"
          style={{
            boxShadow: "0 0 0 4px rgba(139, 92, 246, 0.2), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
          }}
        />

        {/* Tool Handle (optional) */}
        <Handle
          type="source"
          position={Position.Right}
          id="tool"
          className="w-3 h-3 bg-amber-500 border-2 border-white"
          style={{
            top: "20%",
            boxShadow: "0 0 0 4px rgba(245, 158, 11, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
          }}
        />
        <div className="absolute text-[10px] font-semibold text-amber-600" style={{ right: -30, top: "15%" }}>
          Tool
        </div>

        {/* Retry Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="retry"
          className="w-3 h-3 bg-violet-500 border-2 border-white"
          style={{
            top: "50%",
            boxShadow: "0 0 0 4px rgba(139, 92, 246, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
          }}
        />
        <div className="absolute text-[10px] font-semibold text-violet-600" style={{ right: -38, top: "45%" }}>
          Retry
        </div>

        {/* Pass Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="pass"
          className="w-3 h-3 bg-green-500 border-2 border-white"
          style={{
            top: "80%",
            boxShadow: "0 0 0 4px rgba(34, 197, 94, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
          }}
        />
        <div className="absolute text-[10px] font-semibold text-green-600" style={{ right: -32, top: "75%" }}>
          Pass
        </div>
      </div>

      {/* Prompt Editor Modal */}
      <PromptEditorModal
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
        value={instruction}
        onChange={setInstruction}
        title="Edit Evaluation Instruction"
        description="Define the validation criteria the LLM will use to evaluate the extracted data"
        placeholder="e.g., Every bill must have a non-null billno and a valid entry_type (invoice, credit_note, payment_total, or other)."
      />

      {/* Configuration Modal */}
      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="bg-white rounded-lg shadow-2xl w-[95vw] max-w-[1600px] h-[92vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <ShieldCheck className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Validator Configuration
                    </h2>
                    <p className="text-xs text-gray-500">
                      Validate LLM output with iterative evaluation loop
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content (3 Columns) */}
              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                {/* Column 1: Left Sidebar (Previous Nodes) */}
                <div className="w-[400px] shrink-0 overflow-hidden">
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={handleModalDragStart}
                  />
                </div>

                {/* Column 2: Center - Configuration */}
                <div className="flex-1 bg-white overflow-y-auto p-6 min-w-0">
                  <div className="max-w-xl mx-auto space-y-6">
                    {/* Input JSON */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Input JSON to Evaluate{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Drag the output from the parse/extraction node that you want the validator to check.
                      </p>
                      <div
                        onDrop={(e) => handleDrop(e, "input_json")}
                        onDragOver={(e) => {
                          handleDragOver(e);
                          setIsDraggingOver("input_json");
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setIsDraggingOver(null);
                        }}
                        className={`min-h-[80px] w-full p-4 border-2 border-dashed rounded-xl transition-all flex items-center justify-center gap-3 ${
                          isDraggingOver === "input_json"
                            ? "border-violet-400 bg-violet-50"
                            : inputJson
                              ? "border-violet-300 bg-violet-50/50"
                              : "border-gray-300 bg-gray-50 hover:border-violet-300"
                        }`}
                      >
                        {inputJson ? (
                          <div className="flex items-start justify-between gap-3 w-full">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-500 mb-1">
                                Selected source
                              </div>
                              <code className="text-sm text-violet-700 font-mono break-all">
                                {inputJson}
                              </code>
                            </div>
                            <button
                              onClick={() => setInputJson("")}
                              className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex-1 flex flex-col items-center justify-center py-4 text-gray-400 gap-2">
                            <Copy className="w-8 h-8 opacity-50" />
                            <span className="text-sm font-medium">
                              Drag JSON variable here
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Evaluation Instruction */}
                    <div>
                      <label className="text-xs font-medium text-gray-700">
                        Evaluation Instruction{" "}
                        <span className="text-gray-400 font-normal">(Required)</span>
                      </label>
                      <p className="text-[10px] text-gray-400 mb-1.5">
                        Define the validation criteria. Click to edit in full-screen editor.
                      </p>
                      <div
                        onClick={() => setIsPromptModalOpen(true)}
                        onDrop={(e) => handleDrop(e, "instruction")}
                        onDragOver={(e) => {
                          handleDragOver(e);
                          setIsDraggingOver("instruction");
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setIsDraggingOver(null);
                        }}
                        className={`w-full min-h-[100px] p-3 border border-gray-300 text-xs font-mono text-gray-700 bg-gray-50 hover:bg-white hover:border-violet-400 cursor-pointer transition-all group relative ${
                          isDraggingOver === "instruction" ? "ring-2 ring-violet-400 bg-violet-50/30" : ""
                        }`}
                      >
                        {instruction ? (
                          <div className="whitespace-pre-wrap break-words line-clamp-4">
                            {instruction}
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">
                            Click to edit evaluation instruction or drag text here...
                          </span>
                        )}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="bg-white/80 backdrop-blur-sm p-1.5 rounded-md shadow-sm border border-gray-200 text-[10px] font-medium text-violet-600 flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" /> Edit
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Iteration Limit */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Max Iterations
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Maximum retry cycles before the validator passes through. Higher = more attempts to fix issues.
                      </p>
                      <input
                        type="number"
                        value={iterationLimit}
                        onChange={(e) =>
                          setIterationLimit(Math.max(1, Math.min(10, parseInt(e.target.value) || 3)))
                        }
                        min={1}
                        max={10}
                        className="w-32 px-4 py-2.5 border-2 border-gray-300 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition hover:border-violet-300"
                      />
                    </div>
                  </div>
                </div>

                {/* Column 3: Right Sidebar - Outputs */}
                <div className="w-[420px] shrink-0 bg-gray-50 overflow-y-auto p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Node Outputs
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Available after execution.
                  </p>

                  {hasOutputs ? (
                    <div className="mt-2 space-y-3">
                      {/* Status Banner */}
                      <div className={`p-3 rounded-lg border ${
                        data.outputs.is_valid
                          ? "bg-green-50 border-green-200"
                          : data.outputs.iteration >= data.outputs.max_iterations
                            ? "bg-amber-50 border-amber-200"
                            : "bg-red-50 border-red-200"
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-bold ${
                            data.outputs.is_valid ? "text-green-700" : data.outputs.iteration >= data.outputs.max_iterations ? "text-amber-700" : "text-red-700"
                          }`}>
                            {data.outputs.is_valid ? "VALID" : data.outputs.iteration >= data.outputs.max_iterations ? "MAX ITERATIONS" : "INVALID"}
                          </span>
                          <span className="text-xs font-mono text-gray-600">
                            Iter {data.outputs.iteration}/{data.outputs.max_iterations}
                          </span>
                        </div>
                        {data.outputs.quality_score !== undefined && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                              <span>Quality Score</span>
                              <span className="font-bold">{(data.outputs.quality_score * 100).toFixed(0)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${
                                  data.outputs.quality_score >= 0.8 ? "bg-green-500" :
                                  data.outputs.quality_score >= 0.5 ? "bg-amber-500" : "bg-red-500"
                                }`}
                                style={{ width: `${data.outputs.quality_score * 100}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Remarks */}
                      {data.outputs.remarks && (
                        <div className="p-3 bg-white border border-gray-200 rounded-lg">
                          <h4 className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Remarks</h4>
                          <p className="text-xs text-gray-700 whitespace-pre-wrap break-words">
                            {data.outputs.remarks}
                          </p>
                        </div>
                      )}

                      {/* Tool Validation Results */}
                      {data.outputs.tool_validation && data.outputs.tool_validation.length > 0 && (
                        <div className="p-3 bg-white border border-gray-200 rounded-lg">
                          <h4 className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Tool Validation</h4>
                          {data.outputs.tool_validation.map((tool: any, ti: number) => (
                            <div key={ti} className="mb-2">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="font-medium text-gray-700">{tool.tool_name}</span>
                                {tool.iterated && (
                                  <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                    {tool.total_items} items
                                  </span>
                                )}
                              </div>
                              {tool.iterated && Array.isArray(tool.results) ? (
                                <div className="space-y-1 max-h-40 overflow-y-auto">
                                  {tool.results.map((r: any) => (
                                    <div key={r.index} className={`flex items-center gap-2 text-[10px] px-2 py-1 rounded ${
                                      r.result?.status === 200 ? "bg-green-50 text-green-700" :
                                      r.result?.is_error ? "bg-red-50 text-red-700" : "bg-gray-50 text-gray-600"
                                    }`}>
                                      <span className="font-mono w-6">[{r.index}]</span>
                                      <span>{r.result?.status || "?"}</span>
                                      <span className="truncate flex-1">
                                        {r.result?.data?.response_message || r.result?.data?.customer_name || ""}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-[10px] bg-gray-50 rounded p-2 font-mono overflow-x-auto">
                                  {JSON.stringify(tool.result?.data || tool.result, null, 2)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Evaluation History */}
                      {data.outputs.evaluation_history && data.outputs.evaluation_history.length > 0 && (
                        <div className="p-3 bg-white border border-gray-200 rounded-lg">
                          <h4 className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Evaluation History</h4>
                          <div className="space-y-1.5">
                            {data.outputs.evaluation_history.map((h: any, hi: number) => (
                              <div key={hi} className={`flex items-start gap-2 text-[10px] p-2 rounded ${
                                h.is_valid ? "bg-green-50" : "bg-red-50"
                              }`}>
                                <span className={`font-bold mt-0.5 ${h.is_valid ? "text-green-600" : "text-red-600"}`}>
                                  #{h.iteration}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <span className={`font-semibold ${h.is_valid ? "text-green-700" : "text-red-700"}`}>
                                      {h.is_valid ? "PASS" : "FAIL"}
                                    </span>
                                    <span className="text-gray-500">q:{(h.quality_score * 100).toFixed(0)}%</span>
                                  </div>
                                  <p className="text-gray-600 truncate">{h.remarks}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Full JSON (collapsible) */}
                      <details className="bg-white border border-gray-200 rounded-lg">
                        <summary className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-50">
                          Full JSON Output
                        </summary>
                        <div className="p-3 font-mono text-xs overflow-x-auto border-t border-gray-100">
                          <pre className="whitespace-pre-wrap break-words">
                            <code
                              dangerouslySetInnerHTML={{
                                __html: syntaxHighlight(data.outputs),
                              }}
                            />
                          </pre>
                        </div>
                      </details>
                    </div>
                  ) : (
                    <div className="p-4 bg-white border border-gray-200 rounded text-center mt-4">
                      <p className="text-xs text-gray-500 font-medium">No outputs yet</p>
                      <p className="text-xs text-gray-400 mt-1">Execute the node to see results.</p>
                    </div>
                  )}

                  {/* Output Fields Info */}
                  <div className="mt-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Output Fields</h4>
                    <div className="space-y-1.5 text-[11px] text-gray-600">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-violet-500 rounded-full"></div>
                        <span><strong>is_valid</strong> — boolean</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-violet-500 rounded-full"></div>
                        <span><strong>remarks</strong> — feedback string</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-violet-500 rounded-full"></div>
                        <span><strong>validated_result</strong> — best JSON data</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-violet-500 rounded-full"></div>
                        <span><strong>iteration</strong> — iteration count</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-violet-500 rounded-full"></div>
                        <span><strong>evaluation_history</strong> — audit trail</span>
                      </div>
                    </div>
                  </div>

                  {/* Routing Info */}
                  <div className="mt-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Routing</h4>
                    <div className="space-y-1.5 text-[11px] text-gray-600">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                        <span><strong>Tool</strong> — optional external validation (HTTP, Code, etc.)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-violet-500 rounded-full"></div>
                        <span><strong>Retry</strong> — back to source for re-extraction</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        <span><strong>Pass</strong> — valid or max iterations reached</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  {errors.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Configuration Valid</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-red-700">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">{errors.length} issue(s) found</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    disabled={errors.length > 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ValidatorNode;
